import { memo, useRef } from "react";
import { Sparkles, Wand2, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import type { PatchNote } from "../types";

interface PatchNotesModalProps {
  open: boolean;
  /** Notas não vistas, mais recente primeiro. */
  notes: PatchNote[];
  /** Havia mais novidades além das exibidas. */
  hasMore?: boolean;
  onClose: () => void;
}

const SECTIONS = [
  { key: "news", label: "Novidades", icon: Sparkles },
  { key: "improvements", label: "Melhorias", icon: Wand2 },
  { key: "fixes", label: "Correções", icon: Wrench },
] as const;

export const PatchNotesModal = memo(function PatchNotesModal({
  open,
  notes,
  hasMore = false,
  onClose,
}: PatchNotesModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-lg"
        onOpenAutoFocus={(event) => {
          // O foco default do Radix cai no X de fechar; para um modal
          // informativo, o alvo natural é o botão de confirmação.
          // preventScroll: sem ele, focar o botão do rodapé rola o
          // DialogContent (que é o container rolável) até o fim quando as
          // notas excedem a altura — o modal abriria mostrando o final.
          event.preventDefault();
          confirmRef.current?.focus({ preventScroll: true });
        }}
      >
        <DialogHeader>
          <DialogTitle>Novidades do sistema</DialogTitle>
          <DialogDescription>
            Veja o que mudou desde a última vez que você usou o sistema.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {notes.map((note, index) => (
            <div key={note.version} className="space-y-3">
              {index > 0 && <Separator />}
              <p className="text-xs text-muted-foreground">
                Atualização de {note.date}
              </p>
              {SECTIONS.map(({ key, label, icon: Icon }) => {
                const items = note[key];
                if (!items?.length) return null;
                return (
                  <div key={key} className="space-y-1">
                    <p className="flex items-center gap-2 text-sm font-semibold">
                      <Icon className="w-4 h-4 text-primary" />
                      {label}
                    </p>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      {items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ))}
          {hasMore && (
            <p className="text-xs text-muted-foreground">
              E outras melhorias em atualizações anteriores.
            </p>
          )}
        </div>
        <DialogFooter className="mt-6">
          <Button ref={confirmRef} onClick={onClose}>
            Entendi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
